'use client'

import { useState, useMemo, Suspense } from 'react'
import { Inter } from 'next/font/google'
import { AuthContext } from '@/contexts/AuthContext'
import {
  authProvider,
  isAuthorized,
  removeLocalStorageAuth,
  setLocalStorageAuth,
  getRBACProfile,
} from '@/lib/auth/auth'
import { usePathname, useRouter } from 'next/navigation'
import { fetcher } from '@/lib/api/fetch'

const inter = Inter({ subsets: ['latin'] })
const emptyUser = {
  RBACProfile: { permissions: {} },
  isAuthorized: () => false,
}

export default function Root({ children }) {
  const [user, setUser] = useState(emptyUser)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const pathname = usePathname()
  const router = useRouter()

  const signin = (username, password, callback) => {
    return authProvider.signin(async () => {
      const res = await fetch(
        process.env.NEXT_PUBLIC_API_URL + '/api/user/login',
        {
          method: 'POST',
          body: JSON.stringify({ username, password }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      const body = await res.json()

      // The user is only returned when the login is successful
      if (body.success) {
        setuser({ ...emptyUser, ...body.user }, () => {
          setIsAuthenticated(true)
          setLocalStorageAuth(body.user)
          callback({
            status: res.status,
            body,
          })
        })
      } else {
        callback({
          status: res.status,
          body,
        })
      }
    })
  }

  const signout = callback => {
    return authProvider.signout(() => {
      setUser(emptyUser)
      setIsAuthenticated(false)
      removeLocalStorageAuth()
      callback()
    })
  }

  const setuser = (user, callback) => {
    return authProvider.setuser(user, async () => {
      try {
        // Get the RBAC Profile for the user
        const profile = await getRBACProfile({ user, router, pathname })
        user.RBACProfile = profile
        user.isAuthorized = isAuthorized
        user.loaded = true
        setUser(user)
        setIsAuthenticated(true)
        setLocalStorageAuth(user)
      } catch (error) {
        console.error('[Root.setuser] Error fetching RBAC profile:', error)
        // Still set loaded to true so the UI doesn't hang
        user.RBACProfile = { permissions: {} }
        user.isAuthorized = isAuthorized
        user.loaded = true
        setUser(user)
        setIsAuthenticated(true)
        setLocalStorageAuth(user)
      }
      callback()
    })
  }

  const refresh = callback => {
    return authProvider.refresh(async () => {
      // Get the user from the api
      const res = await fetcher(
        `/api/admin/user/${user.id}`,
        { method: 'GET' },
        { user, pathname, router: { push: router.push } },
      )

      if (res.status !== 200) {
        console.error('Failed to refresh user', res)
        return
      }

      const body = await res.json()
      let newUser = {}
      setUser(u => {
        newUser = { ...u, ...body }
        setLocalStorageAuth(newUser)
        return newUser
      })

      // setUser(user)
      if (callback) callback(newUser)
    })
  }

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      signin,
      signout,
      setuser,
      refresh,
      pathname,
      router: { push: router.push },
    }),
    [user, isAuthenticated],
  )

  return (
    <AuthContext.Provider value={value}>
      <html lang="en">
        <Suspense>
          <body className={inter.className}>{children}</body>
        </Suspense>
      </html>
    </AuthContext.Provider>
  )
}
